// QGC-style mission pattern generators.
// Pure functions only: identical math must be mirrored 1:1 in
// command_center/survey/generators.py (see parity tests).

export const M_PER_DEG_LAT = 111319.49;

const DEG = Math.PI / 180;
const ROUND = 8; // decimals kept on generated coordinates (~1 mm)

// Return true if two {lat, lng} points coincide (polygon closing corner).
function samePoint(a, b, tol = 1e-8) {
  return Math.abs(a.lat - b.lat) <= tol && Math.abs(a.lng - b.lng) <= tol;
}

function mPerDegLon(lat0Deg) {
  return 111319.49 * Math.cos(lat0Deg * DEG);
}

function project(lat, lng, origin) {
  return {
    x: (lng - origin.lng) * mPerDegLon(origin.lat),
    y: (lat - origin.lat) * M_PER_DEG_LAT,
  };
}

function unproject(p, origin) {
  return {
    lat: +(origin.lat + p.y / M_PER_DEG_LAT).toFixed(ROUND),
    lng: +(origin.lng + p.x / mPerDegLon(origin.lat)).toFixed(ROUND),
  };
}

function rotate(p, aRad) {
  const c = Math.cos(aRad);
  const s = Math.sin(aRad);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

function unrotate(p, aRad) {
  return rotate(p, -aRad);
}

export function haversineMeters(a, b) {
  const R = 6371008.8;
  const dLat = (b.lat - a.lat) * DEG;
  const dLng = ((b.lng - a.lng) * DEG);
  const la1 = a.lat * DEG;
  const la2 = b.lat * DEG;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function polygonCentroid(vertices) {
  let lat = 0;
  let lng = 0;
  vertices.forEach((v) => {
    lat += v.lat !== undefined ? v.lat : v[0];
    lng += v.lng !== undefined ? v.lng : v[1];
  });
  const n = vertices.length || 1;
  return { lat: lat / n, lng: lng / n };
}

/** Intersections (x values) of horizontal line y=k with polygon edges (even-odd rule). */
function linePolygonIntersectionsX(pts, k) {
  const xs = [];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    if (a.y === b.y) continue; // parallel edge
    if (k >= Math.min(a.y, b.y) && k < Math.max(a.y, b.y)) {
      xs.push(a.x + ((k - a.y) * (b.x - a.x)) / (b.y - a.y));
    }
  }
  return xs.sort((p, q) => p - q);
}

/**
 * Survey grid over a polygon (QGC "Survey" item).
 * vertices: [{lat,lng}...]; params: {gridAngleDeg, spacing, altitude,
 * turnaroundDistance, crossGrid}
 * Convention: gridAngle measured clockwise from North (0 => N-S legs).
 */
export function generateSurveyGrid(vertices, params) {
  const angle = params.gridAngleDeg ?? 90;
  const spacing = Math.max(1, params.spacing ?? 10);
  const altitude = params.altitude ?? 30;
  const turnaround = params.turnaroundDistance ?? 0;
  const passes = [{ angle }];
  if (params.crossGrid) passes.push({ angle: angle + 90 });

  const origin = polygonCentroid(vertices);
  const geoPts = vertices.map((v) => ({
    lat: v.lat !== undefined ? v.lat : v[0],
    lng: v.lng !== undefined ? v.lng : v[1],
  }));

  const waypoints = [];
  passes.forEach(({ angle }) => {
    const alpha = (angle - 90) * DEG; // rotation making transects horizontal
    const poly = geoPts.map((p) => rotate(project(p.lat, p.lng, origin), alpha));
    const ys = poly.map((p) => p.y);
    const ymin = Math.min(...ys);
    const ymax = Math.max(...ys);

    // Collect per-row inside segments (even-odd pairing of crossings).
    const rows = [];
    const rowCount = Math.floor((ymax - ymin) / spacing) + 1;
    for (let r = 0; r <= rowCount; r++) {
      const y = ymin + r * spacing;
      if (y > ymax) break;
      const xs = linePolygonIntersectionsX(poly, y);
      for (let i = 0; i + 1 < xs.length; i += 2) {
        if (xs[i + 1] - xs[i] >= spacing * 0.5) rows.push({ y, x0: xs[i], x1: xs[i + 1] });
      }
    }
    rows.sort((r1, r2) => r1.y - r2.y || r1.x0 - r2.x0);

    rows.forEach((row, idx) => {
      const ltr = idx % 2 === 0;
      const xa = ltr ? row.x0 : row.x1;
      const xb = ltr ? row.x1 : row.x0;
      const pts = [[xa, row.y], [xb, row.y]];
      if (turnaround > 0) {
        const ext = ltr ? turnaround : -turnaround;
        pts.unshift([pts[0][0] - ext, row.y]);
        pts.push([pts[pts.length - 1][0] + ext, row.y]);
      }
      pts.forEach(([px, py]) => {
        const q = unrotate({ x: px, y: py }, alpha);
        const g = unproject(q, origin);
        waypoints.push({ latitude: g.lat, longitude: g.lng, altitude });
      });
    });
  });
  return waypoints;
}

/**
 * Corridor scan along a polyline (QGC "Corridor Scan").
 * Sweeps transects across the corridor width, serpentine-connected.
 */
export function generateCorridorScan(polyline, params) {
  const width = Math.max(2, params.width ?? 20);
  const spacing = Math.max(1, params.spacing ?? 10);
  const altitude = params.altitude ?? 30;
  const path = polyline.map((v) => ({
    lat: v.lat !== undefined ? v.lat : v[0],
    lng: v.lng !== undefined ? v.lng : v[1],
  }));
  if (path.length < 2) return [];

  const origin = polygonCentroid(path);
  const pts = path.map((p) => project(p.lat, p.lng, origin));
  const half = width / 2;
  const out = [];
  let lineIdx = 0;

  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const segLen = Math.hypot(b.x - a.x, b.y - a.y);
    if (segLen < 1e-9) continue;
    const ux = (b.x - a.x) / segLen;
    const uy = (b.y - a.y) / segLen;
    const nx = -uy;
    const ny = ux;
    const steps = Math.max(1, Math.ceil(segLen / spacing));

    for (let s = 0; s <= steps; s++) {
      const t = Math.min(segLen, s * spacing);
      const cx = a.x + ux * t;
      const cy = a.y + uy * t;
      const sideA = { x: cx + nx * half, y: cy + ny * half };
      const sideB = { x: cx - nx * half, y: cy - ny * half };
      const legs = lineIdx % 2 === 0 ? [sideA, sideB] : [sideB, sideA];
      legs.forEach((lp) => {
        const g = unproject(lp, origin);
        out.push({ latitude: g.lat, longitude: g.lng, altitude });
      });
      lineIdx++;
    }
  }
  return out;
}

/**
 * Structure scan: perimeter loop(s) around footprint (QGC "Structure Scan").
 */
export function generateStructureScan(vertices, params) {
  const altitude = params.altitude ?? 30;
  const offset = params.offset ?? 0;
  const levels = Math.max(1, params.levels ?? 1);
  let geo = vertices.map((v) => ({
    lat: v.lat !== undefined ? v.lat : v[0],
    lng: v.lng !== undefined ? v.lng : v[1],
  }));
  if (geo.length < 3) return [];

  // The polygon is stored closed (last vertex repeats the first), so drop the
  // trailing duplicate BEFORE offset so offsetting doesn't re-open a double-
  // counted corner. Each distinct corner is emitted once, then an explicit
  // closing waypoint returns to corner 1 so the drone flies the full box
  // perimeter around the structure before RTL; a 4-corner box = 5 waypoints.
  if (geo.length > 1 && samePoint(geo[0], geo[geo.length - 1])) {
    geo = geo.slice(0, -1);
  }

  let ring = geo;
  if (offset !== 0) {
    const origin = polygonCentroid(geo);
    const prj = geo.map((p) => project(p.lat, p.lng, origin));
    ring = prj.map((cur, i) => {
      const prev = prj[(i - 1 + prj.length) % prj.length];
      const next = prj[(i + 1) % prj.length];
      const n1 = normalOf(prev, cur);
      const n2 = normalOf(cur, next);
      const nx = n1.x + n2.x;
      const ny = n1.y + n2.y;
      const len = Math.hypot(nx, ny) || 1;
      const scale = offset / len;
      return unproject({ x: cur.x + nx * scale, y: cur.y + ny * scale }, origin);
    });
  }

  const out = [];
  for (let lvl = 0; lvl < levels; lvl++) {
    ring.forEach((g, i) => {
      if (lvl > 0 && i === 0) return; // already at start vertex
      out.push({ latitude: g.lat, longitude: g.lng, altitude });
    });
    // close each perimeter loop back at its first corner (full box path)
    out.push({ latitude: ring[0].lat, longitude: ring[0].lng, altitude });
  }
  return out;
}

function normalOf(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: -dy / len, y: dx / len }; // outward for CCW rings
}

/** Flatten typed plan items into one ordered waypoint list (upload payload). */
export function flattenPlanItems(items) {
  const out = [];
  items.forEach((item) => {
    switch (item.kind) {
      case 'waypoint':
        out.push({
          latitude: item.latitude,
          longitude: item.longitude,
          altitude: item.altitude,
          hover_duration: item.hoverDuration ?? 0,
        });
        break;
      case 'survey_grid':
        out.push(...generateSurveyGrid(item.polygon, item.parameters));
        break;
      case 'corridor_scan':
        out.push(...generateCorridorScan(item.polyline, item.parameters));
        break;
      case 'structure_scan':
        out.push(...generateStructureScan(item.polygon, item.parameters));
        break;
      default:
        break;
    }
  });
  return out.map((w, seq) => ({ seq, ...w }));
}

export function missionStats(waypoints) {
  let distance = 0;
  const pts = (waypoints || []).map((w) =>
    w.lat !== undefined ? w : { lat: w.latitude, lng: w.longitude }
  );
  for (let i = 1; i < pts.length; i++) {
    distance += haversineMeters(pts[i - 1], pts[i]);
  }
  return {
    total_distance_m: Math.round(distance * 10) / 10,
    waypoint_count: waypoints.length,
    estimated_time_s: Math.round(distance / 5), // assume 5 m/s survey speed
  };
}

/** Ray-casting point-in-polygon. */export function pointInPolygon(pt, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersect =
      (yi > pt[1]) !== (yj > pt[1]) &&
      pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Derive the effective geofence from fence items (+ legacy polygons).
 * Inclusion and exclusion rings are returned separately so the device can
 * upload a multi-polygon fence (ArduPilot >= 4.1 donut fences).
 */
export function deriveGeofence(planDoc = {}) {
  const g = planDoc.geofence || {};
  const inRings = [];
  const exRings = [];
  for (const it of planDoc.items || []) {
    if (it.kind === 'fence_inclusion') inRings.push(it.polygon);
    else if (it.kind === 'fence_exclusion') exRings.push(it.polygon);
  }
  if (!inRings.length && !exRings.length) {
    // legacy plans: one typed polygons list
    const legacy = Array.isArray(g.polygons) ? g.polygons : [];
    if (g.type === 'exclusion') exRings.push(...legacy);
    else inRings.push(...legacy);
  }
  let type;
  if (inRings.length && exRings.length) type = 'mixed';
  else if (inRings.length) type = 'inclusion';
  else if (exRings.length) type = 'exclusion';
  else type = 'inclusion';
  return {
    enabled: !!g.enabled && inRings.length + exRings.length > 0,
    type,
    action: g.action || 'RTL',
    polygons: inRings,
    exclusions: exRings,
  };
}

/**
 * Validation warnings for a plan before upload.
 * Every waypoint must be inside every inclusion ring AND outside every
 * exclusion ring. Accepts derived geofences or legacy raw docs where a
 * single ``type`` key qualifies the ``polygons`` list.
 */
export function validatePlan(planDoc, waypoints) {
  const warnings = [];
  if (!planDoc.items.length) warnings.push('Plan has no mission items.');
  const g = planDoc.geofence || {};
  let inclusions = g.polygons || [];
  let exclusions = g.exclusions || [];
  if (!g.exclusions && g.type === 'exclusion') {
    exclusions = [...inclusions];
    inclusions = [];
  }
  waypoints.forEach((w, i) => {
    inclusions.forEach((poly, k) => {
      if (!pointInPolygon([w.latitude, w.longitude], poly)) {
        warnings.push(`WP${i + 1} outside inclusion geofence #${k + 1}.`);
      }
    });
    exclusions.forEach((poly, k) => {
      if (pointInPolygon([w.latitude, w.longitude], poly)) {
        warnings.push(`WP${i + 1} inside exclusion geofence #${k + 1}.`);
      }
    });
  });
  if (g.enabled && !inclusions.length && !exclusions.length) {
    warnings.push('Geofence enabled but no fence polygon drawn.');
  }
  return [...new Set(warnings)];
}
